import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { BlueSwitch, WfMenuList, WfMenuListItem } from "@WahooFitness/redesignr";
import { t, Trans } from "@lingui/macro";
import { useNotifications } from "./useNotifications";
import {
  ErrorBoundary,
  GlobalHeader,
  WahooDatePickerLocalizationProvider,
} from "@WahooFitness/wahoo-offline-mfe";
import { TimePicker } from "@mui/x-date-pickers";
import { NotificationEventSettingType } from "@WahooFitness/cloud-client-types";

interface NotificationSettingsProps {
  eventTypeId: number;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ eventTypeId }) => {
  const {
    notificationSettingsLoading,
    notificationSettingsError,
    convertDateToTimeString,
    convertTimeStringToDate,
    getNotificationType,
    getNotificationSettingsForType,
    toggleEmailNotification,
    togglePushNotification,
    updateSendTime,
    notificationUpdateInProgress,
  } = useNotifications();
  const notificationType = getNotificationType(eventTypeId);
  const notificationSettingsForType = getNotificationSettingsForType(eventTypeId);

  const timeSelectionDisplay = (eventId: number, sendTime: string | null) => (
    <WahooDatePickerLocalizationProvider>
      <TimePicker
        value={convertTimeStringToDate(sendTime)}
        onAccept={(newValue) => {
          updateSendTime(eventId, convertDateToTimeString(newValue));
        }}
        slotProps={{
          textField: {
            variant: "outlined",
            size: "small",
            fullWidth: false,
            onKeyDown: onKeyDown,
            inputProps: {
              style: { textAlign: "right" },
            },
          },
          actionBar: {
            actions: [`accept`],
          },
          layout: {
            sx: {
              "& .MuiTimePickerToolbar-hourMinuteLabel": {
                alignItems: "center",
              },
              "& .MuiTimePickerToolbar-hourMinuteLabel .MuiButton-root": {
                overflow: "visible",
              },
            },
          },
        }}
      />
    </WahooDatePickerLocalizationProvider>
  );

  const renderNotificationWfMenuList = (setting: NotificationEventSettingType) => (
    <WfMenuList sx={{ padding: 0 }}>
      <WfMenuListItem
        id={`setting ${setting.notification_event_id}`}
        isHeader={true}
        content={setting.name}
        secondaryContent={setting.description}
      />
      <WfMenuListItem
        id={`push toggle ${setting.notification_event_id}`}
        content={t`Push notification`}
        variant="action"
        primaryRightComponentOverride={<BlueSwitch checked={setting.push_notification} />}
        action={() => togglePushNotification(setting.notification_event_id)}
        disabled={notificationUpdateInProgress}
        hidden={!setting.enabled_push_notification}
      />
      <WfMenuListItem
        id={`email toggle ${setting.notification_event_id}`}
        content={t`Email notification`}
        variant="action"
        primaryRightComponentOverride={<BlueSwitch checked={setting.email} />}
        action={() => toggleEmailNotification(setting.notification_event_id)}
        disabled={notificationUpdateInProgress}
        hidden={!setting.enabled_email}
      />
      <WfMenuListItem
        id={`reminder time ${setting.notification_event_id}`}
        content={t`Reminder time`}
        variant="action"
        hidden={!setting.push_notification || !setting.scheduled}
        primaryRightComponentOverride={timeSelectionDisplay(
          setting.notification_event_id,
          setting.send_time
        )}
      />
    </WfMenuList>
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  return (
    <>
      <GlobalHeader title={notificationType?.name ?? ""} />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <ErrorBoundary error={notificationSettingsError} isLoading={notificationSettingsLoading}>
          <Box display="flex" flexDirection="column" p={2} textAlign="center" gap={3}>
            <Typography variant="prose-sm">
              <Trans>
                Settings for these notifications are shared across any devices where you use Wahoo
                app
              </Trans>
            </Typography>
            {notificationSettingsForType.map((setting) => renderNotificationWfMenuList(setting))}
          </Box>
        </ErrorBoundary>
      </Container>
    </>
  );
};

export { NotificationSettings };
